import { isEmpty } from '@intelligence/v3/utils/common';
import { getFilterControlAdapter } from './controlAdapters';
import { getItemApiParamsFieldMap } from './shared/fieldMapUtils';
import { getStringArrayValue } from './shared/valueUtils';
import {
  buildAppMagicValueFilter,
  buildMetricFilterByDataSource,
  buildSensorTowerValueFilter,
  collectMetricFilterData,
  collectTagFilterData,
  isAppMagicTagField,
  isSensorTowerTagField,
  METRIC_RANGE_FILTER_CONFIG,
} from './apiParamSpecialBuilders';
import { FILTER_TYPE } from './filterTypes';
import { isReleaseRangePickerItem } from './rangePicker/utils';

import type { IFilterItemConfig, IFilterValues } from '@intelligence/v3/filterCore/types';

const isPlainObject = (value: unknown): value is IRecord => {
  return Object.prototype.toString.call(value) === '[object Object]';
};

/**
 * 获取控件 middleware 所需的运行参数。
 * 会把 adapter 默认字段映射和 item 自定义配置合并给后续 URL/API 序列化使用。
 */
export const getFilterMiddlewareParams = (item: IFilterItemConfig): unknown => {
  const controlAdapter = getFilterControlAdapter(item.type);
  const middlewareParams = controlAdapter?.getMiddlewareParams?.(item);
  const defaultFieldMap = controlAdapter?.defaultFieldMap;

  if (isEmpty(defaultFieldMap)) {
    return middlewareParams || null;
  }

  if (isPlainObject(middlewareParams)) {
    return {
      ...middlewareParams,
      defaultFieldMap,
    };
  }

  return {
    defaultFieldMap,
  };
};

/**
 * 对单个筛选字段执行控件级值归一化。
 * URL、短链、继承、loadedValue 和最终 API 组装都会复用这条路径。
 */
export const normalizeFilterValueByItem = (item: IFilterItemConfig, value: unknown): unknown => {
  const controlAdapter = getFilterControlAdapter(item.type);

  if (!controlAdapter?.normalizeValue) {
    return value;
  }

  return controlAdapter.normalizeValue(item, value);
};

/**
 * 按 items 白名单裁剪并归一化筛选器值。
 * 未出现在当前页面 items 中的字段会被丢弃，避免跨页面来源污染当前筛选器。
 */
export const normalizeFilterValuesByItems = (items: IFilterItemConfig[], values?: IFilterValues): IFilterValues => {
  const baseValues = values ?? {};
  const nextValues: IFilterValues = {};

  items?.forEach((item) => {
    if (Object.prototype.hasOwnProperty.call(baseValues, item?.field)) {
      const normalizedValue = normalizeFilterValueByItem(item, baseValues[item?.field]);

      if (normalizedValue !== undefined && normalizedValue !== null) {
        nextValues[item?.field] = normalizedValue as IFilterValues[string];
      }
    }
  });

  return nextValues;
};

const isReleaseFilterItem = (item: IFilterItemConfig): boolean => {
  if (item?.type !== FILTER_TYPE.RANGE_PICKER) {
    return false;
  }

  return isReleaseRangePickerItem(item);
};

const SHOULD_GROUP_API_PARAMS_FILTER_KEYS: string[] = [
  FILTER_TYPE.GENRE_SELECT,
  FILTER_TYPE.COMPANY_SELECT,
  FILTER_TYPE.GAME_ENGINE,
];
const shouldGroupApiParamsInFilter = (item: IFilterItemConfig): boolean => {
  return SHOULD_GROUP_API_PARAMS_FILTER_KEYS.includes(item.type) || isReleaseFilterItem(item);
};

const getFilterApiParamsWithoutInternalFields = (
  item: IFilterItemConfig,
  apiParams: IRecord,
  params?: unknown,
): IRecord | null => {
  const nextApiParams = { ...apiParams };
  const apiParamsFieldMap = getItemApiParamsFieldMap(item, params);

  if (item.type === FILTER_TYPE.GENRE_SELECT) {
    const genreSourceField = apiParamsFieldMap.genreSource || 'genre_source';
    delete nextApiParams[genreSourceField];
  }

  if (isReleaseFilterItem(item)) {
    const granularityFields = [apiParamsFieldMap.granularity, 'granularity', 'release_granularity'].filter(Boolean);

    granularityFields.forEach((field) => {
      delete nextApiParams[field as string];
    });
  }

  return isEmpty(nextApiParams) ? null : nextApiParams;
};

/**
 * 将 Filter 的 field -> value 结构转换为接口入参。
 * 这里只处理最终请求 payload，不参与初始化来源解析、URL 同步或组件 draft 状态。
 */
export const buildFilterApiParams = (items: IFilterItemConfig[], values: IFilterValues): Record<string, any> => {
  if (isEmpty(items)) {
    return {};
  }

  const result: IRecord = {};
  const filterResult: IRecord = {};
  const filterValues: IRecord = values || {};
  const sensorTowerTagFilterData: IRecord<{ value: string[] }> = {};
  const appMagicTagFilterData: IRecord<{ value: string[] }> = {};
  const metricFilterData: IRecord<any[]> = {};
  let dataSources: string[] = [];

  items.forEach((item) => {
    if (isEmpty(item)) {
      return;
    }

    const controlAdapter = getFilterControlAdapter(item.type);

    if (!controlAdapter) {
      return;
    }

    const filterValue = normalizeFilterValueByItem(item, filterValues[item.field]);

    if (item.type === FILTER_TYPE.DATA_SOURCE) {
      dataSources = getStringArrayValue(filterValue);
    }

    if (isSensorTowerTagField(item.field)) {
      collectTagFilterData(sensorTowerTagFilterData, item, filterValue);
      return;
    }

    if (isAppMagicTagField(item.field)) {
      collectTagFilterData(appMagicTagFilterData, item, filterValue);
      return;
    }

    if (
      collectMetricFilterData({
        target: metricFilterData,
        field: item.field,
        type: item.type,
        value: filterValue,
      })
    ) {
      return;
    }

    const middlewareParams = getFilterMiddlewareParams(item);
    const apiParams = controlAdapter.middlewareUtils.convertToApiParams(item, filterValue, middlewareParams);

    if (isEmpty(apiParams)) {
      return;
    }

    if (shouldGroupApiParamsInFilter(item)) {
      const groupedApiParams = getFilterApiParamsWithoutInternalFields(item, apiParams, middlewareParams);

      if (!isEmpty(groupedApiParams)) {
        Object.assign(filterResult, groupedApiParams);
      }

      return;
    }

    Object.assign(result, apiParams);
  });

  if (!isEmpty(sensorTowerTagFilterData)) {
    Object.assign(filterResult, buildSensorTowerValueFilter(sensorTowerTagFilterData));
  }

  Object.keys(sensorTowerTagFilterData).forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(result, field)) {
      delete result[field];
    }
  });

  if (!isEmpty(appMagicTagFilterData)) {
    Object.assign(filterResult, buildAppMagicValueFilter(appMagicTagFilterData));
  }

  const metricFilterDataSources = isEmpty(dataSources) ? getStringArrayValue(filterValues.dataSource) : dataSources;
  const metricFilter = buildMetricFilterByDataSource(metricFilterData, metricFilterDataSources);
  if (!isEmpty(metricFilter)) {
    Object.assign(filterResult, {
      metric_filter: metricFilter,
    });
  }

  if (!isEmpty(filterResult)) {
    Object.assign(result, {
      filter: filterResult,
    });
  }

  return result;
};
